/**
 * Shared list of agent + model combinations the UI lets a user pick
 * for a workflow. Both the marketplace deploy footer and the pipeline
 * detail page's Agent dropdown render this list verbatim so they
 * never drift.
 *
 * Encoding: the option value is the string `<agent>:<model>` (e.g.
 * `"claude:opus-4.6"`). The empty string `""` is the "Project default"
 * sentinel — sends NO override, so the workflow row's aiAgent / aiModel
 * stay absent and the runtime falls back to project.aiAgent +
 * project.aiModel.
 *
 * Keep this in lockstep with packages/core/src/constants.js's
 * CLAUDE_MODEL_MAP / CODEX_MODEL_MAP / GEMINI_MODEL_MAP. The shortlist
 * here intentionally excludes obscure historic variants (o3-mini,
 * codex-mini, etc.) to keep the dropdown readable; users who need
 * them can still set the env via the CLI or ENV tab.
 */

// Labels are `<Vendor> · <Model>`. We can't drop the vendor prefix
// because the same model name reaches different CLIs (Sonnet 4.6 via
// Claude direct vs. via Cursor's router); without the prefix the user
// has no way to tell which strategy will run. Cursor in `auto` mode is
// the only entry whose model dimension is "the router picks" — we
// surface that as "Cursor · Auto" rather than burying it.
export const AGENT_OPTIONS = [
  { value: '',                          label: 'Project default' },

  // Claude (Anthropic API direct)
  { value: 'claude:auto',               label: 'Claude · Sonnet 4.6' },
  { value: 'claude:opus-4.6',           label: 'Claude · Opus 4.6' },
  { value: 'claude:sonnet-4.5',         label: 'Claude · Sonnet 4.5' },
  { value: 'claude:opus-4.5',           label: 'Claude · Opus 4.5' },

  // Cursor (Cursor CLI — auto router picks the underlying model)
  { value: 'cursor:auto',               label: 'Cursor · Auto' },

  // Codex (OpenAI Codex CLI)
  { value: 'codex:o4-mini',             label: 'Codex · o4-mini' },
  { value: 'codex:gpt-5.4',             label: 'Codex · GPT-5.4' },
  { value: 'codex:gpt-5.3',             label: 'Codex · GPT-5.3' },
  { value: 'codex:gpt-5.2-codex',       label: 'Codex · GPT-5.2' },
  { value: 'codex:gpt-5.2',             label: 'Codex · GPT-5.2 base' },
  { value: 'codex:gpt-4o',              label: 'Codex · GPT-4o' },
  { value: 'codex:gpt-4o-mini',         label: 'Codex · GPT-4o mini' },

  // Gemini (Google Gemini CLI)
  { value: 'gemini:gemini-2.5-pro',     label: 'Gemini · 2.5 Pro' },
  { value: 'gemini:gemini-2.5-flash',   label: 'Gemini · 2.5 Flash' },
];

/**
 * Split an option value like `"claude:opus-4.6"` into `{ aiAgent, aiModel }`.
 * Returns `{ aiAgent: null, aiModel: null }` for the empty "project default"
 * sentinel so callers can use `?? null` chains cleanly.
 */
export function parseAgentOption(value) {
  if (!value || typeof value !== 'string') return { aiAgent: null, aiModel: null };
  const idx = value.indexOf(':');
  if (idx < 0) return { aiAgent: value, aiModel: null };
  return {
    aiAgent: value.slice(0, idx) || null,
    aiModel: value.slice(idx + 1) || null,
  };
}

/** Reverse of parseAgentOption — pick the matching option value. */
export function encodeAgentOption({ aiAgent, aiModel }) {
  if (!aiAgent) return '';
  return `${aiAgent}:${aiModel || 'auto'}`;
}
